import { DENIED_CONSENT, toGoogleConsent, type CookieConsent } from '~/lib/cookie-consent';

/**
 * Google Tag Manager, loaded only after consent.
 *
 * Ported from the Academy's `marketing-tracking.ts`, minus the commerce events.
 * The container id is not hard-coded here as it is there: this site resolves it
 * server-side from `GTM_ID` (production builds only, and never on /admin,
 * /review or /healthcheck), and the root loader hands it down.
 *
 * Two layers, deliberately. Consent Mode is set to denied before anything else
 * runs, so a tag that somehow arrives early still has no storage permission;
 * and the container script itself is not injected at all until an optional
 * category is granted. Either alone would be weaker than both.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __bhfConsentModeReady?: boolean;
  }
}

function dataLayer(): unknown[] {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

/** Denied-by-default signals. Idempotent; safe to call on every render. */
export function initializeConsentMode(): void {
  if (typeof window === 'undefined' || window.__bhfConsentModeReady) return;
  window.__bhfConsentModeReady = true;
  window.gtag =
    window.gtag ??
    function gtag(...args: unknown[]) {
      dataLayer().push(args);
    };
  window.gtag('consent', 'default', toGoogleConsent(DENIED_CONSENT));
}

export function updateConsentMode(consent: CookieConsent): void {
  if (typeof window === 'undefined') return;
  initializeConsentMode();
  window.gtag?.('consent', 'update', toGoogleConsent(consent));
}

export function isGtmLoaded(): boolean {
  return typeof document !== 'undefined' && Boolean(document.querySelector('script[data-gtm-id]'));
}

/**
 * Injects the container. Returns false when there is nothing to load, so callers
 * can tell "not configured" from "already there".
 */
export function loadGtm(containerId: string | null): boolean {
  if (typeof document === 'undefined' || !containerId) return false;
  if (isGtmLoaded()) return true;

  dataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  script.dataset.gtmId = containerId;
  document.head.appendChild(script);
  return true;
}
